import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../api";
import { Notice } from "../components/Layout";
import { useI18n } from "../i18n";
import type { PublicConfig, Session } from "../types";

export const QR_REFRESH_MS = 30_000;
export const SWITCH_RETRY_MS = 500;
const MAX_TIMEOUT_MS = 2_147_000_000;
const ABSOLUTE_TIMESTAMP = /(?:Z|[+-]\d{2}:\d{2})$/i;

export function absoluteTimestampMs(value: string): number {
  if (!ABSOLUTE_TIMESTAMP.test(value)) throw new Error("Expected an absolute timestamp with Z or an explicit offset");
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) throw new Error("Invalid absolute timestamp");
  return timestamp;
}

export function nextSwitchDelay(nextSwitchAt: string, now = Date.now()): number {
  return Math.max(0, absoluteTimestampMs(nextSwitchAt) - now);
}

export interface ChallengeResponse {
  ok: true; anchorSessionId: string; session: Session; challenge: string; code: string;
  expiresAt: string; nextSwitchAt: string | null; attendanceCount?: number;
}

interface SwitchTimerOptions {
  now?: () => number;
  setTimer?: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (timer: ReturnType<typeof setTimeout>) => void;
}

export function scheduleClassroomSwitch(nextSwitchAt: string,
  refetch: () => Promise<Pick<ChallengeResponse, "nextSwitchAt"> | null>, options: SwitchTimerOptions = {}) {
  const now = options.now ?? Date.now;
  const setTimer = options.setTimer ?? ((callback, delay) => setTimeout(callback, delay));
  const clearTimer = options.clearTimer ?? ((timer) => clearTimeout(timer));
  let active = true;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const schedule = (delay: number) => {
    timer = setTimer(() => { void refresh(); }, Math.min(delay, MAX_TIMEOUT_MS));
  };
  const refresh = async () => {
    const refreshed = await refetch();
    if (!active || !refreshed?.nextSwitchAt) return;
    if (nextSwitchDelay(refreshed.nextSwitchAt, now()) === 0) schedule(SWITCH_RETRY_MS);
  };
  schedule(nextSwitchDelay(nextSwitchAt, now()));
  return () => {
    active = false;
    if (timer !== null) clearTimer(timer);
  };
}

interface WakeLockSentinelLike extends EventTarget { release(): Promise<void> }
type WakeLockNavigator = Navigator & { wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> } };

export function fullscreenAvailable(documentValue: Pick<Document, "documentElement"> | undefined = typeof document === "undefined" ? undefined : document): boolean {
  return Boolean(documentValue?.documentElement.requestFullscreen);
}

function useScreenWakeLock(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let sentinel: WakeLockSentinelLike | null = null;
    const request = async () => {
      if (!active || document.visibilityState !== "visible") return;
      try {
        const acquired = await (navigator as WakeLockNavigator).wakeLock?.request("screen") ?? null;
        sentinel = acquired;
        acquired?.addEventListener("release", () => { if (sentinel === acquired) sentinel = null; }, { once: true });
      } catch { sentinel = null; }
    };
    const visible = () => { if (document.visibilityState === "visible" && !sentinel) void request(); };
    void request();
    document.addEventListener("visibilitychange", visible);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", visible);
      void sentinel?.release().catch(() => undefined);
    };
  }, [enabled]);
}

export function ClassroomDisplay({ sessionId, kiosk = false, onExit }: {
  sessionId: string; kiosk?: boolean; onExit?: () => void;
}) {
  const { t, toggle } = useI18n();
  const [data, setData] = useState<ChallengeResponse | null>(null);
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(90);
  const [publicAppUrl, setPublicAppUrl] = useState(window.location.origin);
  const requestSequence = useRef(0);
  const load = useCallback(async (): Promise<ChallengeResponse | null> => {
    if (!sessionId) return null;
    const requestId = ++requestSequence.current;
    try {
      const value = await api<ChallengeResponse>(`/api/admin/sessions/${encodeURIComponent(sessionId)}/challenge`);
      if (requestId !== requestSequence.current) return null;
      setData(value);
      setError("");
      return value;
    } catch (reason) {
      if (requestId === requestSequence.current) setError(reason instanceof Error ? reason.message : t("error"));
      return null;
    }
  }, [sessionId, t]);

  useScreenWakeLock(kiosk);
  useEffect(() => {
    api<PublicConfig>("/api/config").then((value) => setPublicAppUrl(value.publicAppUrl)).catch(() => undefined);
  }, []);
  useEffect(() => {
    void load();
    const timer = window.setInterval(load, QR_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);
  useEffect(() => {
    if (!data?.nextSwitchAt) return;
    return scheduleClassroomSwitch(data.nextSwitchAt, load, {
      setTimer: (callback, delay) => window.setTimeout(callback, delay),
      clearTimer: (timer) => window.clearTimeout(timer),
    });
  }, [data?.nextSwitchAt, load]);
  useEffect(() => {
    const timer = window.setInterval(() => setSeconds(data
      ? Math.max(0, Math.ceil((Date.parse(data.expiresAt) - Date.now()) / 1000))
      : 0), 1_000);
    return () => window.clearInterval(timer);
  }, [data]);

  if (!sessionId) return <main className={kiosk ? "kiosk-page" : "display-page"}>
    <Notice kind="error">Choose a class from the teacher admin first.</Notice>
  </main>;
  const studentUrl = data
    ? publicAppUrl.replace(/\/$/, "") + "/check-in?challenge=" + encodeURIComponent(data.challenge)
    : "";
  return <ClassroomDisplayView data={data} studentUrl={studentUrl} seconds={seconds} error={error}
    kiosk={kiosk} fullscreenSupported={fullscreenAvailable()} onFullscreen={() => {
      void document.documentElement.requestFullscreen?.().catch(() => undefined);
    }} onExit={onExit} onToggleLanguage={toggle} />;
}

export function ClassroomDisplayView({ data, studentUrl, seconds, error, kiosk, fullscreenSupported,
  onFullscreen, onExit, onToggleLanguage }: {
  data: ChallengeResponse | null; studentUrl: string; seconds: number; error: string; kiosk: boolean;
  fullscreenSupported: boolean; onFullscreen?: () => void; onExit?: () => void; onToggleLanguage?: () => void;
}) {
  const { t } = useI18n();
  return <main className={kiosk ? "kiosk-page" : "display-page"} data-kiosk={kiosk ? "locked" : undefined}>
    {!kiosk && <button className="display-language" onClick={onToggleLanguage}>{t("switch")}</button>}
    <img className="display-logo" src="/logo.png" alt="Assemble Studio" />
    {kiosk && <div className="kiosk-utility">
      {fullscreenSupported && <button onClick={onFullscreen}>Full screen</button>}
      <button onClick={onExit}>Exit classroom display</button>
    </div>}
    {error && <Notice kind="error">{error}</Notice>}
    {!data && !error && <div className="display-loading">{t("loading")}</div>}
    {data && <>
      <header className="display-heading">
        <h1>{data.session.title}</h1>
        <p className="display-meta"><span>{data.session.start_time}–{data.session.end_time}</span></p>
      </header>
      <div className="display-status" aria-live="polite">
        <span>{data.session.status === "open" ? "Open for check-in" : t("closed")}</span>
        {data.attendanceCount !== undefined && <span>{data.attendanceCount} checked in</span>}
      </div>
      <div className={kiosk ? "kiosk-qr" : "display-grid"}>
        <div className="qr-frame" role="img" aria-label={t("display")}>
          <QRCodeSVG value={studentUrl} size={kiosk ? 560 : 340} level="M" fgColor="#00154a" bgColor="#ffffff" title={t("display")} />
        </div>
        <div className={kiosk ? "kiosk-code" : "display-code"}>
          <span>{t("classCode")}</span><strong>{data.code}</strong>
          <small className="display-refresh"><span>QR refresh</span><span>{seconds}s</span></small>
        </div>
      </div>
    </>}
  </main>;
}